// multi-internet.ts — the 3x3 redundancy mesh, ported from the design attachment's iframe-2
// inline script (lines 3147-3459).
//
// The source ran inside `<iframe srcdoc>`, so it owned a whole document. None of that survives the
// flattening, and the departures are the same ones ./multiconnection.ts already documents:
//
//   * every lookup is `root.querySelector` — 15 element handles plus the nine route paths the
//     module itself builds. All the ids it reaches for are prefixed `tb-mi-` in the markup, for
//     the reason spelled out in ../sections/multi-internet.tsx: the sibling multiconnection
//     document is on the same page and `static-layer` / `packet-layer` / `map-title` are exactly
//     the kind of name two independently authored documents both pick;
//   * the IntersectionObserver (source 3304-3308) is gone: `useSectionAnimation` already gates
//     this module's mount on the section entering the viewport, which is what it did;
//   * `performance.now()` and the source's own rAF loop are gone: every deadline is expressed in
//     the elapsed-ms value `clock` hands out each frame, so teardown can never leave a loop
//     behind and a hidden tab freezes the simulation for free;
//   * `prefers-reduced-motion` arrives as a single `Infinity` frame (engine.ts's contract), which
//     maps onto the source's own `reduced` branch (3300-3303). The source signalled it by adding
//     `reduced-motion` to `<html>`; page-state classes belong to the section/page root here, so
//     the class goes on the section and the stylesheet's rules are compounded onto `.tb-multinet`;
//   * the `visibilitychange` start/stop pair (3307) is dropped — a rAF clock already stops
//     advancing in a hidden tab, so the listener had nothing left to do;
//   * the `?scene=` / `?reduced-motion` URL parameters (3170-3172) are dropped: they were the
//     standalone file's own screenshot harness, and honouring a query string from the trial page's
//     URL would let any visitor freeze the instrument on an "internet down" frame;
//   * `innerHTML` string assembly (with its hand-rolled `esc()`, source 3176, 3226-3237) is
//     replaced by real nodes built through `svgNode`, so nothing here ever parses markup.
//
// Copy split, matching hero.ts and multiconnection.ts: the static no-JS state is page copy and
// comes from `t(...)` in the section; everything this module writes at runtime comes from
// `strings.multiInternetInstrument`. Only the topology's own geometry and its server inventory
// live here, as data.
import { getRtlAwareScrollProgress, scrollToLogicalEdge } from '@/utils/rtl-scroll';

import { TRIAL_BETA_ASSETS } from '../assets';
import { prefersReducedMotion } from './engine';
import type { MultiInternetScenarioKey } from './strings';
import type { SectionModule } from './use-section-animation';

const SVG_NS = 'http://www.w3.org/2000/svg';

export interface MultiInternetBridge {
  /** Full name, used in the route group's aria-label. Source: `bridge[0]`. */
  server: string;
  /** The same name without the `NOPING ` prefix, which is what the card prints. Source: 3193. */
  short: string;
  /** NoPing node the route lands on — the card's headline. Source: `bridge[1]` before the ` · `. */
  node: string;
  /** Route latency in ms; drives both the printed pill and the outbound packet duration. */
  latency: number;
}

export interface MultiInternetNet {
  id: string;
  label: string;
  type: string;
  /** Vertical centre of the internet card, in viewBox units. */
  center: number;
  /** Top of the route band that holds the net's three servers. */
  bandY: number;
  bridges: readonly MultiInternetBridge[];
}

// Source 3174-3178. Three internet links, three dedicated NoPing servers each — the nine routes
// the HUD counts. Exported so the section's tests can assert the mesh the markup implies.
export const MULTI_INTERNET_NETS: readonly MultiInternetNet[] = [
  {
    id: 'i1',
    label: 'INTERNET A',
    type: 'FIBER · 500 MB',
    center: 100,
    bandY: 40,
    bridges: [
      { server: 'NOPING SERVER 01', short: 'SERVER 01', node: 'WASHINGTON 105', latency: 18 },
      { server: 'NOPING SERVER 02', short: 'SERVER 02', node: 'DALLAS 107', latency: 19 },
      { server: 'NOPING SERVER 03', short: 'SERVER 03', node: 'ASHBURN 103', latency: 21 },
    ],
  },
  {
    id: 'i2',
    label: 'INTERNET B',
    type: 'CABLE · 300 MB',
    center: 225,
    bandY: 165,
    bridges: [
      { server: 'NOPING SERVER 04', short: 'SERVER 04', node: 'DALLAS 121', latency: 17 },
      { server: 'NOPING SERVER 05', short: 'SERVER 05', node: 'MIAMI 114', latency: 20 },
      { server: 'NOPING SERVER 06', short: 'SERVER 06', node: 'ATLANTA 108', latency: 22 },
    ],
  },
  {
    id: 'i3',
    label: 'INTERNET C',
    type: '5G · 120 MB',
    center: 350,
    bandY: 290,
    bridges: [
      { server: 'NOPING SERVER 07', short: 'SERVER 07', node: 'WASHINGTON 102', latency: 20 },
      { server: 'NOPING SERVER 08', short: 'SERVER 08', node: 'DALLAS 114', latency: 23 },
      { server: 'NOPING SERVER 09', short: 'SERVER 09', node: 'ASHBURN 121', latency: 26 },
    ],
  },
];

// Source 3241-3252.
const CYCLE_MS = 4200;
const PACKET_INTERVAL_MS = 800;
const PACKET_POOL_SIZE = 3;
const FINAL_PACKET_POOL_SIZE = 5;
const ACCESS_DURATION_MS = 760;
const FANOUT_START_MS = 900;
const INBOUND_DURATION_MS = 600;
const OUTBOUND_START_MS = 1560;
const FASTEST_PING = 17;
const OUTBOUND_FASTEST_MS = 820;
const OUTBOUND_PER_PING_MS = 95;
const FINAL_DURATION_MS = 380;
// Source 3346-3351: the live region stops announcing after a dozen scene changes so a visitor who
// leaves the page open is not read the same six sentences forever.
const ARIA_ANNOUNCEMENT_LIMIT = 12;
const ARIA_MUTE_DELAY_MS = 1200;
// Source 3300-3308: which scene each cycle shows, and in which order.
const SCENARIO_TIMELINE: readonly MultiInternetScenarioKey[] = ['restored', 'i1', 'restored', 'i2', 'restored', 'i3', 'restored', 'i12', 'restored', 'i13', 'restored', 'i23'];
const SCENARIO_DOWN: Record<MultiInternetScenarioKey, readonly string[]> = {
  restored: [],
  i1: ['i1'],
  i2: ['i2'],
  i3: ['i3'],
  i12: ['i1', 'i2'],
  i13: ['i1', 'i3'],
  i23: ['i2', 'i3'],
};

type SvgAttributes = Record<string, string | number>;

function svgNode(tag: string, attributes: SvgAttributes = {}, children: readonly SVGElement[] = []): SVGElement {
  const node = document.createElementNS(SVG_NS, tag);
  for (const [name, value] of Object.entries(attributes)) node.setAttribute(name, String(value));
  for (const child of children) node.appendChild(child);
  return node;
}

function svgLabel(tag: string, attributes: SvgAttributes, text: string): SVGElement {
  const node = svgNode(tag, attributes);
  node.textContent = text;
  return node;
}

function pathAccess(net: MultiInternetNet): string {
  // Source 3181. The middle link runs straight across; the outer two bow towards their band.
  if (net.center === 225) return 'M133 225 C145 224 157 226 169 225';
  return `M133 225 C151 225 151 ${net.center} 169 ${net.center}`;
}

function pathInto(net: MultiInternetNet, cy: number): string {
  return `M300 ${net.center} C329 ${net.center} 334 ${cy} 365 ${cy}`;
}

function pathOut(net: MultiInternetNet, cy: number): string {
  return `M555 ${cy} H726 Q752 ${cy} 786 ${net.center} C836 ${net.center} 852 225 902 225`;
}

function routeDuration(latency: number): number {
  return OUTBOUND_FASTEST_MS + (latency - FASTEST_PING) * OUTBOUND_PER_PING_MS;
}

function scenarioAt(elapsedMs: number): MultiInternetScenarioKey {
  const slot = Math.floor(elapsedMs / CYCLE_MS);
  const { length } = SCENARIO_TIMELINE;
  return SCENARIO_TIMELINE[((slot % length) + length) % length] ?? 'restored';
}

// jsdom implements no SVG geometry engine — `SVGPathElement` does not exist there and
// getTotalLength/getPointAtLength are undefined — so both are probed rather than called blind.
// In a browser they always exist; under test the packets keep their telemetry and their fade and
// only their translate is skipped, which is what lets the module be exercised at all.
interface MeasuredPath {
  element: SVGElement | null;
  length: number;
}

function measurePath(element: SVGElement | null): MeasuredPath {
  const geometry = element as unknown as { getTotalLength?: () => number } | null;
  return { element, length: typeof geometry?.getTotalLength === 'function' ? geometry.getTotalLength() : 0 };
}

function pointAt(path: MeasuredPath, distance: number): { x: number; y: number } | null {
  const geometry = path.element as unknown as { getPointAtLength?: (value: number) => { x: number; y: number } } | null;
  if (typeof geometry?.getPointAtLength !== 'function') return null;
  return geometry.getPointAtLength(distance);
}

interface PacketPair {
  halo: SVGElement;
  core: SVGElement;
}

interface AccessObject {
  net: string;
  path: MeasuredPath;
  packets: PacketPair[];
}

interface RouteObject {
  net: string;
  latency: number;
  duration: number;
  input: MeasuredPath;
  output: MeasuredPath;
  inPackets: PacketPair[];
  outPackets: PacketPair[];
}

interface FinalObject {
  group: SVGElement;
  path: MeasuredPath;
  packets: PacketPair[];
}

// `element.textContent = …` on a callback parameter trips no-param-reassign; the alias is the same
// escape hatch ./multiconnection.ts uses for its own event-bar nodes.
function setText(element: SVGElement | null, text: string): void {
  if (!element) return;
  const node = element;
  node.textContent = text;
}

function fade(progress: number): number {
  return Math.max(0, Math.min(1, progress * 9, (1 - progress) * 9));
}

function bridgeNodes(net: MultiInternetNet, bridge: MultiInternetBridge, index: number, label: string): SVGElement[] {
  // Source 3186-3197.
  const cy = net.bandY + 22 + index * 39;
  const y = cy - 16;
  return [svgNode('path', { id: `tb-mi-in-${net.id}-${index}`, class: 'flow', d: pathInto(net, cy) }), svgNode('path', { id: `tb-mi-out-${net.id}-${index}`, class: 'flow', d: pathOut(net, cy) }), svgNode('g', { class: 'bridge-node', 'data-bridge': `${net.id}-${index}`, role: 'group', 'aria-label': label }, [svgNode('rect', { class: 'bridge-card', x: 365, y, width: 190, height: 32, rx: 2 }), svgNode('g', { class: 'bridge-icon', transform: `translate(376 ${cy - 9})`, 'aria-hidden': 'true' }, [svgNode('rect', { width: 22, height: 8, rx: 1 }), svgNode('circle', { class: 'bridge-light', cx: 17, cy: 4, r: 1.5 }), svgNode('rect', { y: 11, width: 22, height: 8, rx: 1 }), svgNode('circle', { class: 'bridge-light', cx: 17, cy: 15, r: 1.5 })]), svgLabel('text', { class: 'bridge-name', x: 407, y: cy - 4 }, bridge.node), svgLabel('text', { class: 'bridge-state', 'data-bridge-state': '', x: 407, y: cy + 8 }, ''), svgLabel('text', { class: 'latency', 'data-latency': '', x: 545, y: cy + 3 }, '')])];
}

function netNodes(net: MultiInternetNet, groupLabel: string, bandTitle: string, routeGroupTitle: string, bridgeLabels: readonly string[]): SVGElement {
  // Source 3199-3216.
  const cardY = net.center - 32;
  const children: SVGElement[] = [
    svgNode('rect', { class: 'band', x: 151, y: net.bandY, width: 751, height: 120, rx: 5 }),
    svgLabel('text', { class: 'band-title', x: 166, y: net.bandY + 14 }, bandTitle),
    svgLabel('text', { class: 'band-sub', x: 785, y: net.bandY + 14 }, routeGroupTitle),
    svgNode('rect', { class: 'internet-card', x: 166, y: cardY, width: 134, height: 64 }),
    svgNode('rect', { class: 'internet-ambient', x: 167, y: cardY + 1, width: 132, height: 62 }),
    svgNode('path', { class: 'internet-corner', d: `M166 ${cardY + 10}V${cardY}H176 M290 ${cardY + 64}H300V${cardY + 54}` }),
    svgNode('circle', { class: 'internet-led', cx: 286, cy: cardY + 13, r: 3 }),
    svgNode('g', { class: 'internet-symbol', transform: `translate(177 ${net.center - 25})`, 'aria-hidden': 'true' }, [svgNode('circle', { class: 'symbol-disc', cx: 14, cy: 14, r: 11 }), svgNode('path', { d: 'M3 14h22M14 3c-3 3.1-4.6 6.8-4.6 11S11 21.9 14 25M14 3c3 3.1 4.6 6.8 4.6 11S17 21.9 14 25' })]),
    svgLabel('text', { class: 'internet-title', x: 215, y: net.center - 16 }, net.label),
    svgLabel('text', { class: 'internet-type', x: 208, y: net.center - 1 }, net.type),
    svgNode('rect', { class: 'state-pill', x: 174, y: net.center + 10, width: 116, height: 17, rx: 8.5 }),
    svgNode('circle', { class: 'state-dot', cx: 184, cy: net.center + 18.5, r: 2 }),
    svgLabel('text', { class: 'state-text', 'data-net-state': '', x: 192, y: net.center + 21 }, ''),
    svgNode('rect', { class: 'internet-scan', x: 167, y: cardY + 62.5, width: 34, height: 1.5, rx: 0.75 }),
  ];
  net.bridges.forEach((bridge, bridgeIndex) => {
    children.push(...bridgeNodes(net, bridge, bridgeIndex, bridgeLabels[bridgeIndex] ?? ''));
  });
  children.push(svgNode('circle', { class: 'junction', cx: 786, cy: net.center, r: 5 }));
  children.push(svgNode('circle', { class: 'junction-core', cx: 786, cy: net.center, r: 1.8 }));
  return svgNode('g', { class: 'net-group', 'data-net': net.id, role: 'group', 'aria-label': groupLabel }, children);
}

function computerNodes(groupLabel: string, title: string, sub: string): SVGElement {
  // Source 3218-3220.
  return svgNode('g', { class: 'computer', role: 'group', 'aria-label': groupLabel }, [
    svgNode('rect', { class: 'pc-card', x: 28, y: 182, width: 108, height: 86, rx: 3 }),
    svgNode('path', { class: 'card-corner', d: 'M28 195V182H41M123 268H136V255' }),
    svgNode('svg', { class: 'endpoint-glyph computer-glyph', x: 56, y: 188, width: 52, height: 52, viewBox: '0 0 64 64', fill: 'none', 'aria-hidden': 'true' }, [svgNode('path', { class: 'glyph-glow', d: 'M5 14h35v26H5zM45 7h14v47H45z' }), svgNode('rect', { class: 'glyph-shell', x: 4.5, y: 13.5, width: 35, height: 26, rx: 2.5 }), svgNode('rect', { class: 'glyph-panel', x: 8.5, y: 17.5, width: 27, height: 17.5, rx: 1 }), svgNode('path', { class: 'glyph-detail', d: 'M10 33.5l8-8 5 4 6-6 5 4M22 40v7m-7 2h14m-19 4h25' }), svgNode('rect', { class: 'glyph-shell', x: 44.5, y: 6.5, width: 15, height: 48, rx: 2.5 }), svgNode('path', { class: 'glyph-panel', d: 'M48.5 11h7v12h-7z' }), svgNode('path', { class: 'glyph-detail', d: 'M48.5 28h7m-7 4h7m-7 4h7' }), svgNode('circle', { class: 'glyph-led', cx: 52, cy: 47.5, r: 1.65 }), svgNode('circle', { class: 'glyph-led', cx: 56, cy: 47.5, r: 0.8, opacity: 0.48 })]),
    svgLabel('text', { class: 'node-label', x: 82, y: 250, 'text-anchor': 'middle' }, title),
    svgLabel('text', { class: 'node-sub', x: 82, y: 262, 'text-anchor': 'middle' }, sub),
  ]);
}

function hubNodes(groupLabel: string, title: string, sub: string, routesLive: string, forwarded: string): SVGElement[] {
  // Source 3221-3224.
  // The traffic badge grows leftwards, never rightwards: the hub card starts at x 902 and
  // the badge's right edge stays at 895. At 78 units wide the routes line ran to x 87.9
  // inside the box and spilled past its right border (SCRUM-1871); 100 units leaves 12
  // units of slack for longer translations. Nothing static sits left of x 746 at this
  // height, and the packet layer paints above this one, so the packets stay visible.
  return [
    svgNode('g', { class: 'hub', role: 'group', 'aria-label': groupLabel }, [
      svgNode('rect', { class: 'hub-card', x: 902, y: 178, width: 136, height: 94, rx: 5 }),
      svgNode('svg', { class: 'endpoint-glyph server-glyph', x: 944, y: 184, width: 52, height: 52, viewBox: '0 0 64 64', fill: 'none', 'aria-hidden': 'true' }, [svgNode('path', { class: 'glyph-glow', d: 'M17 5.5h30l3 3v47l-3 3H17l-3-3v-47z' }), svgNode('path', { class: 'glyph-shell', d: 'M17 5.5h30l3 3v47l-3 3H17l-3-3v-47z' }), svgNode('path', { class: 'glyph-panel', d: 'M19 11h26v11H19zM19 26.5h26v11H19zM19 42h26v11H19z' }), svgNode('path', { class: 'glyph-detail', d: 'M23 15h13m-13 4h8m-8 11.5h13m-13 4h8M23 46h13m-13 4h8' }), svgNode('circle', { class: 'glyph-led', cx: 41, cy: 16.5, r: 1.6 }), svgNode('circle', { class: 'glyph-led', cx: 41, cy: 32, r: 1.6 }), svgNode('circle', { class: 'glyph-led', cx: 41, cy: 47.5, r: 1.6 }), svgNode('path', { class: 'glyph-detail', d: 'M20 58.5V61m24-2.5V61M20 61h24' })]),
      svgLabel('text', { class: 'node-kicker', x: 970, y: 253, 'text-anchor': 'middle' }, title),
      svgLabel('text', { class: 'node-sub', x: 970, y: 265, 'text-anchor': 'middle' }, sub),
    ]),
    svgNode('g', { class: 'traffic-badge', transform: 'translate(795 182)', 'aria-hidden': 'true' }, [svgNode('rect', { width: 100, height: 37, rx: 4 }), svgNode('circle', { cx: 11, cy: 11, r: 2.2 }), svgLabel('text', { class: 'live-routes', id: 'tb-mi-badge-routes', x: 19, y: 13 }, routesLive), svgLabel('text', { x: 11, y: 28 }, forwarded)]),
  ];
}

function gameNodes(groupLabel: string, title: string, sub: string, live: string, gameplayLabel: string, videoSrc: string, poster: string): SVGElement {
  // The live pill is 40 units wide, not the source's 23: its label starts at x 1089 and
  // runs to 1112.4, so at 23 the text overflowed its own pill by 11.4 units in every
  // locale (SCRUM-1884). 40 ends the pill at 1118, leaving the text inside with room to
  // spare and still well clear of the game frame's right edge at 1162.

  // Source 3225-3228. The gameplay loop rides in a foreignObject, so its subtree is XHTML, not SVG.
  const video = document.createElement('video');
  video.className = 'game-video';
  video.autoplay = true;
  video.muted = true;
  video.loop = true;
  video.playsInline = true;
  video.preload = 'metadata';
  video.poster = poster;
  video.setAttribute('aria-label', gameplayLabel);
  const source = document.createElement('source');
  source.src = videoSrc;
  source.type = 'video/mp4';
  video.appendChild(source);
  const media = document.createElement('div');
  media.className = 'game-media';
  media.appendChild(video);

  const foreignObject = svgNode('foreignObject', { class: 'game-media-frame', x: 1073, y: 191, width: 87, height: 50, 'clip-path': 'url(#tb-mi-game-clip)' });
  foreignObject.appendChild(media);

  return svgNode('g', { class: 'game', role: 'group', 'aria-label': groupLabel }, [svgNode('rect', { class: 'game-frame', x: 1071, y: 180, width: 91, height: 90, rx: 3 }), foreignObject, svgNode('rect', { class: 'game-shade', x: 1073, y: 191, width: 87, height: 50, fill: '#030505', opacity: 0.18 }), svgNode('rect', { x: 1078, y: 186, width: 40, height: 11, rx: 5.5, fill: '#09110b', stroke: '#93ff18', 'stroke-width': 0.6 }), svgNode('circle', { class: 'game-live-dot', cx: 1084, cy: 191.5, r: 1.6 }), svgLabel('text', { class: 'game-sub', x: 1089, y: 194 }, live), svgLabel('text', { class: 'game-label', x: 1116.5, y: 254, 'text-anchor': 'middle' }, title), svgLabel('text', { class: 'game-sub', x: 1116.5, y: 266, 'text-anchor': 'middle' }, sub)]);
}

// The game node's gameplay loop (source 3227) — the same asset the multiconnection section plays,
// extracted from the attachment's own manifest and mapped in ../assets.
const GAMEPLAY_VIDEO = TRIAL_BETA_ASSETS.gameplayLoop;
const GAMEPLAY_POSTER = TRIAL_BETA_ASSETS.gameFortnite;

export const multiInternetAnimation: SectionModule = ({ root, strings, clock }) => {
  const copy = strings.multiInternetInstrument;

  const instrument = root.querySelector<HTMLElement>('#tb-mi-network-instrument');
  const staticLayer = root.querySelector<SVGElement>('#tb-mi-static-layer');
  const endpointTrackLayer = root.querySelector<SVGElement>('#tb-mi-endpoint-track-layer');
  const packetLayer = root.querySelector<SVGElement>('#tb-mi-packet-layer');
  const shell = root.querySelector<HTMLElement>('#tb-mi-diagram-shell');
  const activeLinks = root.querySelector<HTMLElement>('#tb-mi-active-links');
  const activeRoutes = root.querySelector<HTMLElement>('#tb-mi-active-routes');
  const systemState = root.querySelector<HTMLElement>('#tb-mi-system-state');
  const eventTitle = root.querySelector<HTMLElement>('#tb-mi-event-title');
  const eventDetail = root.querySelector<HTMLElement>('#tb-mi-event-detail');
  const cycleStatus = root.querySelector<HTMLElement>('#tb-mi-cycle-status');
  const ariaStatus = root.querySelector<HTMLElement>('#tb-mi-aria-status');
  const guideLabel = root.querySelector<HTMLElement>('#tb-mi-guide-label');
  const guideProgress = root.querySelector<HTMLElement>('#tb-mi-guide-progress');
  const guideAction = root.querySelector<HTMLElement>('#tb-mi-guide-action');

  if (!instrument || !staticLayer || !endpointTrackLayer || !packetLayer || !shell) return () => {};
  if (!activeLinks || !activeRoutes || !systemState || !eventTitle || !eventDetail || !cycleStatus || !ariaStatus) return () => {};
  if (!guideLabel || !guideProgress || !guideAction) return () => {};

  // ---- static layer (source 3230-3241) -----------------------------------------------------
  staticLayer.appendChild(computerNodes(copy.computerGroupLabel, copy.computerTitle, copy.computerSub));
  MULTI_INTERNET_NETS.forEach((net, index) => {
    const bridgeLabels = net.bridges.map((bridge) => copy.bridgeGroupLabel(bridge.server, `${bridge.node} · ${bridge.latency}MS`, net.label));
    staticLayer.appendChild(netNodes(net, copy.netGroupLabel(net.label), copy.bandTitle(net.label), copy.routeGroupTitle(index + 1), bridgeLabels));
  });
  for (const node of hubNodes(copy.hubGroupLabel, copy.hubTitle, copy.hubSub, copy.routesLive(9), copy.packetsForwarded)) staticLayer.appendChild(node);
  staticLayer.appendChild(gameNodes(copy.gameGroupLabel, copy.gameTitle, copy.gameSub, copy.gameLive, copy.gameplayLabel, GAMEPLAY_VIDEO, GAMEPLAY_POSTER));

  const firstNet = MULTI_INTERNET_NETS[0];
  const lastNet = MULTI_INTERNET_NETS[2];
  if (!firstNet || !lastNet) return () => {};

  endpointTrackLayer.appendChild(svgNode('g', { class: 'endpoint-motion-guides' }, [...MULTI_INTERNET_NETS.map((net) => svgNode('path', { id: `tb-mi-access-${net.id}`, class: 'endpoint-motion-path', d: pathAccess(net) })), svgNode('path', { id: 'tb-mi-final-path', class: 'endpoint-motion-path', d: 'M1035 225 H1074' })]));
  endpointTrackLayer.appendChild(svgNode('g', { class: 'endpoint-visible-tracks' }, [svgNode('path', { id: 'tb-mi-track-access-i1', class: 'endpoint-visible-track access-track', d: pathAccess(firstNet) }), svgNode('rect', { id: 'tb-mi-track-access-i2', class: 'endpoint-visible-solid access-track', x: 133, y: 223.25, width: 36, height: 3.5, rx: 1.75 }), svgNode('path', { id: 'tb-mi-track-access-i3', class: 'endpoint-visible-track access-track', d: pathAccess(lastNet) }), svgNode('rect', { id: 'tb-mi-track-final', class: 'endpoint-visible-solid final-track', x: 1035, y: 223.25, width: 39, height: 3.5, rx: 1.75 })]));

  const gameVideo = staticLayer.querySelector<HTMLVideoElement>('.game-video');

  // The source (3244) muted-and-played the loop on every start. Autoplay can still be refused, and
  // that rejection is the browser reporting the visitor's own policy — there is nothing to recover
  // from, so it is swallowed exactly as the source swallowed it.
  function playGameplay(): void {
    if (!gameVideo) return;
    gameVideo.muted = true;
    const started = gameVideo.play?.();
    if (started && typeof started.catch === 'function') started.catch(() => {});
  }

  // ---- packet pools (source 3255-3295) -----------------------------------------------------
  function packetPair(group: SVGElement, radius: number): PacketPair {
    const halo = svgNode('circle', { class: 'packet packet-halo', r: radius * 2 });
    const core = svgNode('circle', { class: 'packet', r: radius });
    halo.setAttribute('style', 'opacity:0');
    core.setAttribute('style', 'opacity:0');
    group.appendChild(halo);
    group.appendChild(core);
    return { halo, core };
  }

  function packetTrain(group: SVGElement, radius: number, size = PACKET_POOL_SIZE): PacketPair[] {
    const train: PacketPair[] = [];
    for (let index = 0; index < size; index += 1) train.push(packetPair(group, radius));
    group.setAttribute('data-emission-interval-ms', String(PACKET_INTERVAL_MS));
    return train;
  }

  const accessObjects: AccessObject[] = [];
  const packetObjects: RouteObject[] = [];

  for (const net of MULTI_INTERNET_NETS) {
    const accessGroup = svgNode('g', { 'data-packet-net': net.id, 'data-packet-segment': 'computer-to-internet', 'data-start-ms': '0', 'data-duration-ms': String(ACCESS_DURATION_MS) });
    packetLayer.appendChild(accessGroup);
    accessObjects.push({ net: net.id, path: measurePath(root.querySelector<SVGElement>(`#tb-mi-access-${net.id}`)), packets: packetTrain(accessGroup, 3) });

    net.bridges.forEach((bridge, bridgeIndex) => {
      const duration = routeDuration(bridge.latency);
      const inbound = svgNode('g', { 'data-packet-net': net.id, 'data-packet-segment': 'internet-to-bridge', 'data-start-ms': String(FANOUT_START_MS), 'data-duration-ms': String(INBOUND_DURATION_MS) });
      const outbound = svgNode('g', { 'data-packet-net': net.id, 'data-packet-segment': 'bridge-to-final', 'data-start-ms': String(OUTBOUND_START_MS), 'data-duration-ms': String(duration), 'data-ping': String(bridge.latency) });
      packetLayer.appendChild(inbound);
      packetLayer.appendChild(outbound);
      packetObjects.push({
        net: net.id,
        latency: bridge.latency,
        duration,
        input: measurePath(root.querySelector<SVGElement>(`#tb-mi-in-${net.id}-${bridgeIndex}`)),
        output: measurePath(root.querySelector<SVGElement>(`#tb-mi-out-${net.id}-${bridgeIndex}`)),
        inPackets: packetTrain(inbound, 2.8),
        outPackets: packetTrain(outbound, 3),
      });
    });
  }

  const finalGroup = svgNode('g', {
    'data-packet-net': 'final',
    'data-packet-segment': 'final-to-game',
    'data-aggregation': 'first-copy-forwarded',
    'data-copies-out': '1',
    'data-duration-ms': String(FINAL_DURATION_MS),
  });
  packetLayer.appendChild(finalGroup);
  const finalObject: FinalObject = { group: finalGroup, path: measurePath(root.querySelector<SVGElement>('#tb-mi-final-path')), packets: packetTrain(finalGroup, 3.2, FINAL_PACKET_POOL_SIZE) };

  // ---- scene state (source 3297-3354) ------------------------------------------------------
  let lastScene: MultiInternetScenarioKey | '' = '';
  let ariaAnnouncements = 0;
  let ariaMuteAt = Infinity;
  const linkActiveSince = new Map<string, number>(MULTI_INTERNET_NETS.map((net) => [net.id, 0]));

  function activeSinceAt(netId: string, elapsedMs: number): number {
    const { length } = SCENARIO_TIMELINE;
    const slot = Math.floor(elapsedMs / CYCLE_MS);
    const current = SCENARIO_TIMELINE[((slot % length) + length) % length] ?? 'restored';
    if ((SCENARIO_DOWN[current] ?? []).includes(netId)) return Infinity;
    for (let offset = 0; offset < length; offset += 1) {
      const absoluteSlot = slot - offset;
      const currentKey = SCENARIO_TIMELINE[((absoluteSlot % length) + length) % length] ?? 'restored';
      const previousKey = SCENARIO_TIMELINE[(((absoluteSlot - 1) % length) + length) % length] ?? 'restored';
      const currentUp = !(SCENARIO_DOWN[currentKey] ?? []).includes(netId);
      const previousDown = (SCENARIO_DOWN[previousKey] ?? []).includes(netId);
      if (currentUp && previousDown) return Math.max(0, absoluteSlot * CYCLE_MS);
    }
    return 0;
  }

  function applyScene(scene: MultiInternetScenarioKey, elapsedMs: number): void {
    const elapsed = Number.isFinite(elapsedMs) ? elapsedMs : 0;
    const down = SCENARIO_DOWN[scene] ?? [];
    for (const net of MULTI_INTERNET_NETS) linkActiveSince.set(net.id, activeSinceAt(net.id, elapsed));
    if (scene === lastScene) return;

    for (const net of MULTI_INTERNET_NETS) {
      const isDown = down.includes(net.id);
      const group = root.querySelector<SVGElement>(`[data-net="${net.id}"]`);
      root.querySelector<SVGElement>(`#tb-mi-track-access-${net.id}`)?.classList.toggle('is-paused', isDown);
      if (group) {
        group.classList.toggle('is-down', isDown);
        setText(group.querySelector<SVGElement>('[data-net-state]'), isDown ? copy.netStateInterrupted : copy.netStateOnline);
        group.querySelectorAll<SVGElement>('[data-bridge-state]').forEach((element, index) => {
          const bridge = net.bridges[index];
          if (bridge) setText(element, isDown ? copy.bridgeStateNoSignal(bridge.short) : copy.bridgeStateLive(bridge.short));
        });
        group.querySelectorAll<SVGElement>('[data-latency]').forEach((element, index) => {
          const bridge = net.bridges[index];
          if (bridge) setText(element, isDown ? copy.latencyOffline : `${bridge.latency}MS`);
        });
        group.setAttribute('aria-label', isDown ? copy.netInterruptedLabel(net.label) : copy.netOnlineLabel(net.label));
      }
    }

    const active = MULTI_INTERNET_NETS.length - down.length;
    const routes = active * 3;
    activeLinks!.textContent = copy.activeLinks(active);
    activeRoutes!.textContent = copy.activeRoutes(routes);
    const badge = root.querySelector<SVGElement>('#tb-mi-badge-routes');
    if (badge) badge.textContent = copy.routesLive(routes);
    systemState!.textContent = down.length ? copy.systemStateDegraded : copy.systemStateFull;
    instrument!.dataset.alert = down.length ? 'true' : 'false';
    instrument!.dataset.scene = scene;
    const scenarioCopy = copy.scenarios[scene];
    eventTitle!.textContent = scenarioCopy.title;
    eventDetail!.textContent = scenarioCopy.detail;
    cycleStatus!.textContent = scenarioCopy.indicator;
    cycleStatus!.dataset.state = scene;
    ariaStatus!.textContent = scenarioCopy.aria;
    if (ariaAnnouncements < ARIA_ANNOUNCEMENT_LIMIT) ariaAnnouncements += 1;
    else if (ariaAnnouncements === ARIA_ANNOUNCEMENT_LIMIT && scene === 'restored') {
      ariaAnnouncements += 1;
      ariaMuteAt = elapsed + ARIA_MUTE_DELAY_MS;
    }
    lastScene = scene;
  }

  // ---- packet motion (source 3356-3411) ----------------------------------------------------
  function setPacket(pair: PacketPair, path: MeasuredPath, progress: number, opacity: number): void {
    const point = pointAt(path, Math.max(0, Math.min(1, progress)) * path.length);
    const nodes: Array<[SVGElement, number]> = [
      [pair.halo, 0.72],
      [pair.core, 1],
    ];
    for (const [node, weight] of nodes) {
      if (point) node.setAttribute('transform', `translate(${point.x} ${point.y})`);
      node.setAttribute('style', `opacity:${opacity * weight}`);
    }
  }

  function setPacketTrain(packets: PacketPair[], path: MeasuredPath, elapsedMs: number, start: number, duration: number, activeSince: number): void {
    const trainSpan = PACKET_INTERVAL_MS * packets.length;
    packets.forEach((pair, index) => {
      const local = elapsedMs - start - index * PACKET_INTERVAL_MS;
      const trainCycle = local >= 0 ? Math.floor(local / trainSpan) : -1;
      const progress = trainCycle >= 0 ? (local - trainCycle * trainSpan) / duration : -1;
      const batchEmission = trainCycle >= 0 ? index * PACKET_INTERVAL_MS + trainCycle * trainSpan : -1;
      const visible = Number.isFinite(activeSince) && batchEmission >= activeSince && progress >= 0 && progress <= 1;
      setPacket(pair, path, visible ? progress : 0, visible ? fade(progress) : 0);
    });
  }

  function winningRoute(scene: MultiInternetScenarioKey): RouteObject | null {
    const down = SCENARIO_DOWN[scene] ?? [];
    return packetObjects.filter((route) => !down.includes(route.net)).reduce<RouteObject | null>((fastest, route) => (!fastest || route.duration < fastest.duration ? route : fastest), null);
  }

  function setFinalPacketTrain(elapsedMs: number, currentScene: MultiInternetScenarioKey): void {
    const trainSpan = PACKET_INTERVAL_MS * finalObject.packets.length;
    let visibleWinner: RouteObject | null = null;
    finalObject.group.setAttribute('data-copies-in', String(9 - (SCENARIO_DOWN[currentScene] ?? []).length * 3));
    finalObject.packets.forEach((pair, index) => {
      const firstEmission = index * PACKET_INTERVAL_MS;
      const trainCycle = Math.floor((elapsedMs - firstEmission) / trainSpan);
      const batchEmission = trainCycle >= 0 ? firstEmission + trainCycle * trainSpan : -1;
      const batchScene = batchEmission >= 0 ? scenarioAt(batchEmission) : currentScene;
      const winner = batchEmission >= 0 ? winningRoute(batchScene) : null;
      const start = winner ? batchEmission + OUTBOUND_START_MS + winner.duration : -1;
      const progress = start >= 0 ? (elapsedMs - start) / FINAL_DURATION_MS : -1;
      const visible = Boolean(winner) && progress >= 0 && progress <= 1;
      setPacket(pair, finalObject.path, visible ? progress : 0, visible ? fade(progress) : 0);
      if (!visible || !winner) return;
      visibleWinner = winner;
      finalObject.group.setAttribute('data-source-net', winner.net);
      finalObject.group.setAttribute('data-ping', String(winner.latency));
      finalObject.group.setAttribute('data-start-ms', String(start));
      finalObject.group.setAttribute('data-batch-emission-ms', String(batchEmission));
      finalObject.group.setAttribute('data-copies-in', String(9 - (SCENARIO_DOWN[batchScene] ?? []).length * 3));
    });
    if (visibleWinner) return;
    const currentWinner = winningRoute(currentScene);
    if (!currentWinner) return;
    finalObject.group.setAttribute('data-source-net', currentWinner.net);
    finalObject.group.setAttribute('data-ping', String(currentWinner.latency));
  }

  // ---- mobile scroll guide (source 3413-3429) ----------------------------------------------
  function updateGuide(): void {
    // Not `scrollLeft / max`: under the `dir="rtl"` /sa inherits, `scrollLeft` runs -max..0, so the raw
    // ratio pinned the bar at 0% and the stage label at guideStages[0] for the whole scroll.
    const progress = getRtlAwareScrollProgress(shell!);
    guideProgress!.style.width = `${(progress * 100).toFixed(1)}%`;
    // Source 3417-3419: which third of the horizontal scroll the visitor is looking at.
    const [nearStage, midStage, farStage] = copy.guideStages;
    let stage = nearStage;
    if (progress > 0.72) stage = farStage;
    else if (progress > 0.28) stage = midStage;
    guideLabel!.textContent = stage.label;
    guideAction!.textContent = stage.action;
  }

  const listeners = new AbortController();
  const { signal } = listeners;

  shell.addEventListener('scroll', updateGuide, { passive: true, signal });
  shell.addEventListener(
    'keydown',
    (event) => {
      const amount = Math.max(180, shell.clientWidth * 0.7);
      const behavior: ScrollBehavior = prefersReducedMotion() ? 'auto' : 'smooth';
      if (event.key === 'ArrowRight') {
        shell.scrollBy({ left: amount, behavior });
        event.preventDefault();
      }
      if (event.key === 'ArrowLeft') {
        shell.scrollBy({ left: -amount, behavior });
        event.preventDefault();
      }
      // Home/End address the LOGICAL edges: in RTL the start is the right-hand one and the end sits at
      // a negative offset, so the literal `left: 0` / `left: scrollWidth` pair had the two keys swapped.
      if (event.key === 'Home') {
        scrollToLogicalEdge(shell, 'start', behavior);
        event.preventDefault();
      }
      if (event.key === 'End') {
        scrollToLogicalEdge(shell, 'end', behavior);
        event.preventDefault();
      }
    },
    { signal }
  );

  let resizeObserver: ResizeObserver | null = null;
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => updateGuide());
    resizeObserver.observe(shell);
  } else {
    window.addEventListener('resize', updateGuide, { passive: true, signal });
  }

  // ---- frame (source 3431-3442) ------------------------------------------------------------
  let running = false;

  function applyReduced(): void {
    root.classList.add('reduced-motion');
    applyScene('restored', 0);
    instrument!.dataset.motion = 'static';
    gameVideo?.pause();
    ariaStatus!.textContent = copy.reducedMotionAria;
  }

  function step(elapsedMs: number): void {
    if (!Number.isFinite(elapsedMs)) {
      applyReduced();
      return;
    }
    if (!running) {
      running = true;
      instrument!.dataset.motion = 'running';
      playGameplay();
    }
    const scene = scenarioAt(elapsedMs);
    applyScene(scene, elapsedMs);
    if (elapsedMs >= ariaMuteAt) {
      ariaStatus!.setAttribute('aria-live', 'off');
      ariaMuteAt = Infinity;
    }
    for (const access of accessObjects) setPacketTrain(access.packets, access.path, elapsedMs, 0, ACCESS_DURATION_MS, linkActiveSince.get(access.net) ?? 0);
    for (const route of packetObjects) {
      setPacketTrain(route.inPackets, route.input, elapsedMs, FANOUT_START_MS, INBOUND_DURATION_MS, linkActiveSince.get(route.net) ?? 0);
      setPacketTrain(route.outPackets, route.output, elapsedMs, OUTBOUND_START_MS, route.duration, linkActiveSince.get(route.net) ?? 0);
    }
    setFinalPacketTrain(elapsedMs, scene);
  }

  // Source 3444-3445: paint the resting frame before the first tick, so the instrument is never
  // shown with empty route cards.
  applyScene('restored', 0);
  updateGuide();

  const unsubscribe = clock.subscribe(step);

  return () => {
    unsubscribe();
    listeners.abort();
    resizeObserver?.disconnect();
    gameVideo?.pause();
    running = false;
    root.classList.remove('reduced-motion');
    delete instrument.dataset.motion;
    staticLayer.textContent = '';
    endpointTrackLayer.textContent = '';
    packetLayer.textContent = '';
  };
};
